import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { b2bFilterContacts, b2bGetContactOptions, b2bGetLists, b2bCreateList, b2bAddListMembers } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmt$(n) {
  if (!n) return '—';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(0) + 'K';
  return '$' + n;
}

// Multi-select chip picker
function ChipPicker({ label, options, selected, onChange, searchable }) {
  const [search, setSearch] = useState('');
  const filtered = search ? options.filter(o => o.toLowerCase().includes(search.toLowerCase())) : options;

  function toggle(val) {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  }

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={s.filterLabel}>{label}</div>
      {searchable && options.length > 8 && (
        <input style={{ ...s.inp, marginBottom: 6 }} placeholder={`Search ${label.toLowerCase()}…`}
          value={search} onChange={e => setSearch(e.target.value)} />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 130, overflowY: 'auto' }}>
        {filtered.map(o => (
          <button key={o} style={{ ...s.chip, ...(selected.includes(o) ? s.chipActive : {}) }}
            onClick={() => toggle(o)} title={o}>
            {o.length > 28 ? o.slice(0, 26) + '…' : o}
          </button>
        ))}
        {filtered.length === 0 && <span style={{ color: '#94a3b8', fontSize: 11 }}>No matches</span>}
      </div>
      {selected.length > 0 && (
        <button style={s.clearChips} onClick={() => onChange([])}>Clear ({selected.length} selected)</button>
      )}
    </div>
  );
}

export default function ListBuilder() {
  const navigate = useNavigate();

  // Options from DB
  const [titleOptions,         setTitleOptions]         = useState([]);
  const [industryOptions,      setIndustryOptions]      = useState([]);
  const [countryOptions,       setCountryOptions]       = useState([]);
  const [standardTitleOptions, setStandardTitleOptions] = useState([]);

  // Filters — multi-select
  const [selectedStandardTitles, setSelectedStandardTitles] = useState([]);
  const [selectedTitles,         setSelectedTitles]         = useState([]);
  const [selectedIndustries,     setSelectedIndustries]     = useState([]);
  const [country,      setCountry]      = useState('');
  const [state,        setState]        = useState('');
  const [minRevenue,   setMinRevenue]   = useState('');
  const [maxRevenue,   setMaxRevenue]   = useState('');
  const [minEmployees, setMinEmployees] = useState('');
  const [maxEmployees, setMaxEmployees] = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [notInAnyList, setNotInAnyList] = useState(false);
  const [limit,        setLimit]        = useState('200');

  // Results
  const [contacts, setContacts] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);
  const [error,    setError]    = useState('');

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Save to list
  const [lists,       setLists]       = useState([]);
  const [showSave,    setShowSave]    = useState(false);
  const [saveMode,    setSaveMode]    = useState('existing');
  const [targetList,  setTargetList]  = useState('');
  const [newListName, setNewListName] = useState('');
  const [saving,      setSaving]      = useState(false);
  const [saveResult,  setSaveResult]  = useState(null);

  useEffect(() => {
    b2bGetContactOptions().then(r => {
      setTitleOptions(r.titles || []);
      setIndustryOptions(r.industries || []);
      setCountryOptions(r.countries || []);
      setStandardTitleOptions(r.standard_titles || []);
    }).catch(() => {});
    b2bGetLists().then(r => setLists(r.lists || [])).catch(() => {});
  }, []);

  async function runFilter() {
    setLoading(true); setError(''); setSearched(true); setSelected(new Set()); setSaveResult(null);
    try {
      const params = {
        standard_titles: selectedStandardTitles.join(','),
        titles:          selectedTitles.join(','),
        industries:      selectedIndustries.join(','),
        country,
        state,
        min_revenue:   minRevenue   ? parseFloat(minRevenue)   * 1e6 : '',
        max_revenue:   maxRevenue   ? parseFloat(maxRevenue)   * 1e6 : '',
        min_employees: minEmployees,
        max_employees: maxEmployees,
        date_from:     dateFrom,
        date_to:       dateTo,
        not_in_list:   notInAnyList ? '1' : '',
        limit,
      };
      const r = await b2bFilterContacts(params);
      setContacts(r.contacts || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  function clearFilters() {
    setSelectedStandardTitles([]); setSelectedTitles([]); setSelectedIndustries([]); setCountry(''); setState('');
    setMinRevenue(''); setMaxRevenue(''); setMinEmployees(''); setMaxEmployees('');
    setDateFrom(''); setDateTo(''); setNotInAnyList(false); setLimit('200');
    setContacts([]); setSearched(false); setSelected(new Set()); setError('');
  }

  function toggleContact(id) {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function selectAll()   { setSelected(new Set(contacts.map(c => c.contact_id))); }
  function deselectAll() { setSelected(new Set()); }

  async function handleSave() {
    setSaving(true); setSaveResult(null);
    try {
      let listId;
      if (saveMode === 'new') {
        if (!newListName.trim()) { alert('Enter a list name'); setSaving(false); return; }
        const r = await b2bCreateList({ list_name: newListName.trim() });
        listId = r.list_id;
      } else {
        listId = parseInt(targetList);
      }
      const r = await b2bAddListMembers(listId, [...selected]);
      setSaveResult({ added: r.added, listId });
      setShowSave(false);
      setSelected(new Set());
      b2bGetLists().then(r => setLists(r.lists || [])).catch(() => {});
    } catch (e) { alert(e.message); }
    setSaving(false);
  }

  const hasFilters = selectedStandardTitles.length > 0 || selectedTitles.length > 0 || selectedIndustries.length > 0 || country || state ||
                     minRevenue || maxRevenue || minEmployees || maxEmployees || dateFrom || dateTo || notInAnyList;

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-lists" />
      <main style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/b2b/lists')}>← Lists</button>

        <div style={s.header}>
          <h1 style={s.title}>List Builder</h1>
          <p style={s.subtitle}>Filter contacts by any combination of criteria, select who you want, then save to a list.</p>
        </div>

        <div style={s.layout}>
          {/* Filter panel */}
          <div style={s.filterPanel}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={s.filterTitle}>Filters</span>
              {hasFilters && <button style={s.clearAllBtn} onClick={clearFilters}>Clear All</button>}
            </div>

            <ChipPicker
              label="STANDARD TITLE (CATEGORY)"
              options={standardTitleOptions}
              selected={selectedStandardTitles}
              onChange={setSelectedStandardTitles}
              searchable={false}
            />

            <ChipPicker
              label="RAW TITLE / ROLE"
              options={titleOptions}
              selected={selectedTitles}
              onChange={setSelectedTitles}
              searchable
            />

            <ChipPicker
              label="INDUSTRY"
              options={industryOptions}
              selected={selectedIndustries}
              onChange={setSelectedIndustries}
              searchable
            />

            <div style={{ marginBottom: 18 }}>
              <div style={s.filterLabel}>LOCATION</div>
              {countryOptions.length > 0 ? (
                <select style={s.inp} value={country} onChange={e => setCountry(e.target.value)}>
                  <option value="">Any country</option>
                  {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input style={s.inp} placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} />
              )}
              <input style={{ ...s.inp, marginTop: 6 }} placeholder="State / Region" value={state} onChange={e => setState(e.target.value)} />
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={s.filterLabel}>ANNUAL REVENUE (millions)</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={{ ...s.inp, flex: 1 }} type="number" placeholder="Min $M" value={minRevenue} onChange={e => setMinRevenue(e.target.value)} />
                <span style={{ color: '#94a3b8', fontSize: 12 }}>–</span>
                <input style={{ ...s.inp, flex: 1 }} type="number" placeholder="Max $M" value={maxRevenue} onChange={e => setMaxRevenue(e.target.value)} />
              </div>
              <div style={s.quickBtns}>
                {[['<$10M', '', '10'], ['$10–50M', '10', '50'], ['$50–200M', '50', '200'], ['$200M+', '200', '']].map(([l, mn, mx]) => (
                  <button key={l} style={s.quickBtn} onClick={() => { setMinRevenue(mn); setMaxRevenue(mx); }}>{l}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={s.filterLabel}>EMPLOYEES</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={{ ...s.inp, flex: 1 }} type="number" placeholder="Min" value={minEmployees} onChange={e => setMinEmployees(e.target.value)} />
                <span style={{ color: '#94a3b8', fontSize: 12 }}>–</span>
                <input style={{ ...s.inp, flex: 1 }} type="number" placeholder="Max" value={maxEmployees} onChange={e => setMaxEmployees(e.target.value)} />
              </div>
              <div style={s.quickBtns}>
                {[['<100', '1', '100'], ['100–500', '100', '500'], ['500–2K', '500', '2000'], ['2K+', '2000', '']].map(([l, mn, mx]) => (
                  <button key={l} style={s.quickBtn} onClick={() => { setMinEmployees(mn); setMaxEmployees(mx); }}>{l}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={s.filterLabel}>DATE ADDED</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...s.inp, flex: 1 }} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                <input style={{ ...s.inp, flex: 1 }} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={notInAnyList} onChange={e => setNotInAnyList(e.target.checked)} style={{ width: 14, height: 14, cursor: 'pointer' }} />
                <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>Not in any list</span>
              </label>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={s.filterLabel}>MAX RESULTS</div>
              <select style={s.inp} value={limit} onChange={e => setLimit(e.target.value)}>
                {['10', '25', '50', '100', '200', '500'].map(n => <option key={n} value={n}>{n} contacts</option>)}
              </select>
            </div>

            <button style={s.runBtn} onClick={runFilter} disabled={loading}>
              {loading ? 'Searching…' : '🔍 Find Contacts'}
            </button>
          </div>

          {/* Results panel */}
          <div style={s.resultsPanel}>
            {!searched && !loading && (
              <div style={s.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                <div style={{ color: NAVY, fontWeight: 600, fontSize: 15 }}>Set filters and click Find Contacts</div>
                <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>Results will appear here</div>
              </div>
            )}

            {loading && (
              <div style={s.emptyState}>
                <div style={{ color: '#64748b', fontSize: 14 }}>Searching…</div>
              </div>
            )}

            {error && <div style={s.errorBox}>{error}</div>}

            {searched && !loading && !error && (
              <>
                <div style={s.resultsHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span style={{ color: NAVY, fontSize: 14, fontWeight: 600 }}>
                      {contacts.length} contact{contacts.length !== 1 ? 's' : ''} found
                    </span>
                    {contacts.length > 0 && (
                      <>
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{selected.size} selected</span>
                        <button style={s.linkBtn} onClick={selectAll}>Select All</button>
                        {selected.size > 0 && <button style={s.linkBtn} onClick={deselectAll}>Deselect All</button>}
                      </>
                    )}
                  </div>
                  {selected.size > 0 && (
                    <button style={s.saveBtn} onClick={() => setShowSave(true)}>
                      Save {selected.size} to List →
                    </button>
                  )}
                </div>

                {saveResult && (
                  <div style={s.successBanner}>
                    ✓ Added {saveResult.added} contact{saveResult.added !== 1 ? 's' : ''} to the list.{' '}
                    <button style={{ background: 'none', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}
                      onClick={() => navigate(`/admin/b2b/lists/${saveResult.listId}`)}>View list →</button>
                  </div>
                )}

                {contacts.length === 0 && (
                  <div style={s.emptyState}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>😕</div>
                    <div style={{ color: NAVY, fontWeight: 600 }}>No contacts match these filters</div>
                    <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Try broadening your criteria</div>
                  </div>
                )}

                {contacts.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={{ ...s.th, width: 36 }}>
                            <input type="checkbox"
                              checked={selected.size === contacts.length}
                              onChange={e => e.target.checked ? selectAll() : deselectAll()} />
                          </th>
                          {['Name', 'Title', 'Category', 'Company', 'Revenue', 'Employees', 'Location', 'Added'].map(h => (
                            <th key={h} style={s.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map(c => (
                          <tr key={c.contact_id}
                            style={{ ...s.tr, background: selected.has(c.contact_id) ? 'rgba(243,116,40,0.04)' : undefined }}
                            onClick={() => toggleContact(c.contact_id)}>
                            <td style={s.td} onClick={e => e.stopPropagation()}>
                              <input type="checkbox" checked={selected.has(c.contact_id)} onChange={() => toggleContact(c.contact_id)} />
                            </td>
                            <td style={{ ...s.td, fontWeight: 500, color: NAVY, whiteSpace: 'nowrap' }}>
                              {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                            </td>
                            <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || '—'}</td>
                            <td style={{ ...s.td, fontSize: 11 }}>
                              {c.standard_title ? (
                                <span style={{ background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 4, fontWeight: 600, padding: '2px 7px', whiteSpace: 'nowrap' }}>{c.standard_title}</span>
                              ) : '—'}
                            </td>
                            <td style={{ ...s.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name || '—'}</td>
                            <td style={{ ...s.td, fontSize: 12, color: '#059669', whiteSpace: 'nowrap' }}>{fmt$(c.annual_revenue)}</td>
                            <td style={{ ...s.td, fontSize: 12, color: '#64748b' }}>{c.employee_count?.toLocaleString() || '—'}</td>
                            <td style={{ ...s.td, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{[c.city, c.company_state].filter(Boolean).join(', ') || '—'}</td>
                            <td style={{ ...s.td, fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(c.created_on_utc).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Save modal */}
        {showSave && (
          <div style={s.modal}>
            <div style={s.modalBox}>
              <h2 style={{ margin: '0 0 16px', color: NAVY, fontSize: 18 }}>
                Save {selected.size} Contact{selected.size !== 1 ? 's' : ''} to List
              </h2>
              <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                {[['existing', 'Add to Existing List'], ['new', 'Create New List']].map(([mode, label]) => (
                  <button key={mode} style={{ flex: 1, padding: '9px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: saveMode === mode ? 600 : 400, background: saveMode === mode ? NAVY : '#f8fafc', color: saveMode === mode ? '#fff' : '#64748b' }}
                    onClick={() => setSaveMode(mode)}>{label}</button>
                ))}
              </div>
              {saveMode === 'existing' ? (
                <select style={s.inp} value={targetList} onChange={e => setTargetList(e.target.value)}>
                  <option value="">Select a list…</option>
                  {lists.map(l => <option key={l.list_id} value={l.list_id}>{l.list_name} ({l.member_count} contacts)</option>)}
                </select>
              ) : (
                <input style={s.inp} placeholder="New list name…" value={newListName} onChange={e => setNewListName(e.target.value)} autoFocus />
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button style={s.cancelBtn} onClick={() => setShowSave(false)}>Cancel</button>
                <button style={s.primaryBtn} onClick={handleSave}
                  disabled={saving || (saveMode === 'existing' && !targetList) || (saveMode === 'new' && !newListName.trim())}>
                  {saving ? 'Saving…' : 'Save to List'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page:         { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:         { flex: 1, background: '#f0f5fb', padding: '28px 32px', overflowY: 'auto' },
  backBtn:      { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 6px', display: 'block' },
  header:       { marginBottom: 20 },
  title:        { color: NAVY, fontSize: 22, fontWeight: 700, margin: '0 0 4px' },
  subtitle:     { color: '#64748b', fontSize: 13, margin: 0 },
  layout:       { display: 'flex', gap: 20, alignItems: 'flex-start' },
  filterPanel:  { width: 280, flexShrink: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  filterTitle:  { color: NAVY, fontSize: 13, fontWeight: 700 },
  filterLabel:  { color: '#374151', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 },
  inp:          { padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, width: '100%', boxSizing: 'border-box' },
  chip:         { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, color: '#64748b', cursor: 'pointer', fontSize: 11, fontWeight: 500, padding: '3px 8px', textAlign: 'left' },
  chipActive:   { background: 'rgba(243,116,40,0.1)', border: `1px solid ${ORANGE}`, color: ORANGE, fontWeight: 700 },
  clearChips:   { background: 'none', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 10, padding: '4px 0', display: 'block' },
  quickBtns:    { display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' },
  quickBtn:     { background: '#f1f5f9', border: 'none', borderRadius: 4, color: '#64748b', cursor: 'pointer', fontSize: 10, fontWeight: 500, padding: '3px 7px' },
  clearAllBtn:  { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 11, padding: 0 },
  runBtn:       { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '10px', width: '100%', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  resultsPanel: { flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, minHeight: 400, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' },
  resultsHeader:{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  emptyState:   { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, padding: 40 },
  table:        { width: '100%', borderCollapse: 'collapse' },
  th:           { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '9px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  tr:           { borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
  td:           { color: '#374151', fontSize: 13, padding: '9px 14px', verticalAlign: 'middle' },
  saveBtn:      { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  linkBtn:      { background: 'none', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: 0 },
  successBanner:{ background: 'rgba(5,150,105,0.08)', borderBottom: '1px solid rgba(5,150,105,0.15)', color: '#059669', fontSize: 13, fontWeight: 500, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 8 },
  errorBox:     { background: 'rgba(239,68,68,0.06)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '16px 20px', margin: 20 },
  modal:        { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox:     { background: '#fff', borderRadius: 12, padding: '28px 32px', width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  primaryBtn:   { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  cancelBtn:    { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
};
